// components/settings/surfox-settings/sections/SystemSection.jsx
// System Tools - Export Tools + Audit Logs in single scrollable view
// No sub-tabs - flattened UI per Settings V2 philosophy

import React from 'react';
import { 
  Download,
  FileText,
  Shield
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { PERMISSIONS } from '../../../../lib/permissions';
import ExportTools from '../../ExportTools';
import AuditLogViewer from '../../AuditLogViewer';

// ============================================================================
// SECTION CARD COMPONENT
// ============================================================================

const SectionCard = ({ icon: Icon, iconColor, title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-5 py-4 border-b border-slate-100 flex items-center space-x-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${iconColor}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h4 className="font-semibold text-slate-900">{title}</h4>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SystemSection({ onUpdate }) {
  const { hasPermission, loading: authLoading } = useAuth();

  // Permission checks - adjust based on your actual permissions
  const canExport = hasPermission(PERMISSIONS.EXPORT_DATA) || hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);
  const canViewAudit = hasPermission(PERMISSIONS.VIEW_AUDIT_LOGS) || hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Export Tools Section */}
      <SectionCard
        icon={Download}
        iconColor="from-blue-500 to-blue-600"
        title="Export Tools"
        subtitle="Download leads, conversations, and reports"
      >
        <ExportTools />
      </SectionCard>

      {/* Audit Log Section */}
      <SectionCard
        icon={Shield}
        iconColor="from-violet-500 to-violet-600"
        title="Audit Logs"
        subtitle="Track system activity and changes"
      >
        <AuditLogViewer />
      </SectionCard>
    </div>
  );
}